import { z } from 'zod';

const jsonParseIfJson = (input: unknown) => {
  if (typeof input !== 'string') return input;
  try {
    return JSON.parse(input) as unknown;
  } catch {
    return input;
  }
};

const schemaVersion1 = z.literal(1);

const templateStreamDeltaSchema = z.object({
  schemaVersion: schemaVersion1,
  kind: z.literal('delta'),
  text: z.string(),
});

const templateStreamErrorSchema = z.object({
  schemaVersion: schemaVersion1,
  kind: z.literal('error'),
  error: z.string(),
  message: z.string().optional(),
});

const templateStreamDoneSchema = z.object({
  schemaVersion: schemaVersion1,
  kind: z.literal('done'),
});

/** Rollout: previous wire shape `{ data: string }` from format_chunk */
const templateStreamLegacySchema = z.object({
  data: z.string(),
});

const templateStreamModernSchema = z.discriminatedUnion('kind', [
  templateStreamDeltaSchema,
  templateStreamErrorSchema,
  templateStreamDoneSchema,
]);

/**
 * Parsed SSE `data` for template-creation-record streams (Option C + legacy).
 */
export const templateStreamEnvelopeSchema = z.preprocess(
  jsonParseIfJson,
  z.union([templateStreamModernSchema, templateStreamLegacySchema])
);

export type TemplateStreamEnvelope = z.infer<typeof templateStreamEnvelopeSchema>;

/** Align with ml-scribe `ERR_UNSUPPORTED_TEMPLATE_STRUCTURE_REQUEST` */
export const ERR_UNSUPPORTED_TEMPLATE_STRUCTURE_REQUEST =
  'ErrUnsupportedTemplateStructureRequest';

/** Align with ml-scribe `ERR_SECTION_CONVERSION_GUARDRAIL_FAILED` */
export const ERR_SECTION_CONVERSION_GUARDRAIL_FAILED =
  'ErrSectionConversionGuardrailFailed';

/** Align with ml-scribe `ERR_TEMPLATE_STREAM_UPSTREAM_TIMEOUT` */
export const ERR_TEMPLATE_STREAM_UPSTREAM_TIMEOUT = 'ErrTemplateStreamUpstreamTimeout';

/**
 * Opt-in to ml-scribe v1 SSE envelopes (`kind: delta|error|done`). Omit header for
 * legacy `{data}` chunks. Must match `utils/stream_template_creation.py`
 * (`TEMPLATE_STREAM_WIRE_FORMAT_*`).
 *
 * v1 contract, both sides: a stream that completes successfully ends with a
 * `kind: done` frame, and a failure ends with a `kind: error` frame. Section
 * conversion rejects a v1 stream that closes without either as incomplete;
 * bumping the wire format is the only sanctioned way to change that.
 */
export const TEMPLATE_CREATION_STREAM_WIRE_FORMAT_HEADER = 'X-Heidi-Template-Stream';
export const TEMPLATE_CREATION_STREAM_WIRE_FORMAT_V1 = '1';

/** Delta payload for query cache / append helpers (unchanged shape). */
export type StreamDeltaPayload = { data: string };
